import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ensureMonthInList, extendMonths, monthsAround } from '../utils/month.js';
import {
  isAtScrollTarget,
  nextExtensionEdge,
  pickActiveKey,
  scrollTargetFor,
} from '../utils/scroll.js';

/** Gap left below the top of the scroll area when jumping to something. */
const SCROLL_GAP = 12;

/**
 * Drives the endlessly scrollable calendar.
 *
 * The list is made of month sections. It starts on the current month plus one
 * either side and grows by whole months in whichever direction you scroll.
 * The scroll container is the <main> element, not the window — the header
 * stays put and only the calendar moves.
 *
 * @param {string} currentMonthKey month key of today
 * @param {string} todayKey date key of today
 */
export function useEndlessCalendar(currentMonthKey, todayKey) {
  const [months, setMonths] = useState(() => monthsAround(currentMonthKey));
  const [activeMonthKey, setActiveMonthKey] = useState(currentMonthKey);
  // Whether the view already sits exactly where "Today" would take it.
  const [atToday, setAtToday] = useState(true);

  /** The scrolling <main>. Attach with `ref={scrollRef}`. */
  const scrollRef = useRef(null);
  // monthKey -> section node, filled in by the scroller via registerSection.
  const sections = useRef(new Map());
  // Visual order of the sections. A prepend does not reorder the Map, so the
  // ordered list of keys is tracked separately.
  const order = useRef(months);
  // Set just before a prepend so we can restore the visual position after it.
  const anchor = useRef(null);
  const frame = useRef(0);

  const registerSection = useCallback((monthKey, node) => {
    if (node) sections.current.set(monthKey, node);
    else sections.current.delete(monthKey);
  }, []);

  /** Viewport y of the top edge of the scroll area. */
  const containerTop = () => scrollRef.current?.getBoundingClientRect().top ?? 0;

  /** Where the container would have to scroll to put `element` at the top. */
  const targetFor = (element) => {
    const container = scrollRef.current;
    if (!element || !container) return null;
    const offset = element.getBoundingClientRect().top - containerTop() - SCROLL_GAP;
    return scrollTargetFor(
      container.scrollTop,
      offset,
      container.scrollHeight - container.clientHeight
    );
  };

  const todayElement = () => document.getElementById(`day-${todayKey}`);

  /** Scroll an element to the top of the scroll area. */
  const scrollElementIntoView = (element, behavior = 'smooth') => {
    const container = scrollRef.current;
    const top = targetFor(element);
    if (top == null) return;
    // A long jump feels better instantly than as a long smooth glide.
    const far = Math.abs(top - container.scrollTop) > 2500;
    container.scrollTo({ top, behavior: far ? 'auto' : behavior });
  };

  /** Recompute the header month and the Today button state. */
  const syncFromScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Measure the sections in visual order, then let the pure helper decide.
    const measured = [];
    for (const monthKey of order.current) {
      const node = sections.current.get(monthKey);
      if (!node) continue;
      const rect = node.getBoundingClientRect();
      measured.push({ key: monthKey, top: rect.top, bottom: rect.bottom });
    }

    const active = pickActiveKey(measured, containerTop() + SCROLL_GAP + 1);
    if (active) setActiveMonthKey((prev) => (prev === active ? prev : active));

    // Today's card may not be rendered at all if you've scrolled years away.
    const target = targetFor(todayElement());
    setAtToday(target != null && isAtScrollTarget(container.scrollTop, target));
  }, []);

  /** Grow the month list when the scroll area nears either end. */
  const maybeExtend = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    setMonths((prev) => {
      const edge = nextExtensionEdge({
        scrollTop: container.scrollTop,
        viewportHeight: container.clientHeight,
        contentHeight: container.scrollHeight,
        sectionCount: prev.length,
      });
      if (!edge) return prev;

      if (edge === 'start') {
        // Remember where the current first section sits so the view does not
        // jump when months are inserted above it.
        const firstKey = prev[0];
        const node = sections.current.get(firstKey);
        if (node) anchor.current = { key: firstKey, top: node.getBoundingClientRect().top };
      }
      return extendMonths(prev, edge);
    });
  }, []);

  // Scroll listener on the container, throttled to one animation frame.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;

    const onScroll = () => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        syncFromScroll();
        maybeExtend();
      });
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [syncFromScroll, maybeExtend]);

  // After the month list changes: refresh the visual order, then undo the
  // layout shift caused by any sections added above the viewport.
  useLayoutEffect(() => {
    order.current = months;

    if (anchor.current) {
      const node = sections.current.get(anchor.current.key);
      const container = scrollRef.current;
      if (node && container) {
        container.scrollBy(0, node.getBoundingClientRect().top - anchor.current.top);
      }
      anchor.current = null;
    }
  }, [months]);

  /** Bring today's card back to the top of the view. */
  const goToToday = useCallback(
    (behavior = 'smooth') => {
      setActiveMonthKey(currentMonthKey);
      setMonths((prev) => ensureMonthInList(prev, currentMonthKey));
      // Wait a frame so any newly added months are in the DOM.
      requestAnimationFrame(() => {
        scrollElementIntoView(todayElement(), behavior);
        // The smooth scroll fires its own events; this covers the instant case.
        requestAnimationFrame(syncFromScroll);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentMonthKey, todayKey, syncFromScroll]
  );

  // On first paint, put today's card at the top — this is what the mobile
  // view opens on.
  useLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      scrollElementIntoView(todayElement(), 'auto');
      syncFromScroll();
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { months, activeMonthKey, atToday, registerSection, scrollRef, goToToday };
}
